import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import type { User } from "../api/types";
import { useT } from "../i18n";
import { useApp, useEngine, useSyncState } from "../store";
import { RealtimeClient } from "../sync/realtime";
import { onBecameVisible, VisibilitySyncThrottle } from "../sync/triggers";
import { TaskDetail } from "../tasks/TaskDetail";
import { IconSidebar } from "../ui/icons";
import { CompletedView, FiltersLabelsView, FilterView, LabelView } from "../views/FiltersLabels";
import { ProjectView } from "../views/ProjectView";
import { TodayView } from "../views/TodayView";
import { UpcomingView } from "../views/UpcomingView";
import { useKeyboardShortcuts } from "./keyboard";
import { QuickAddModal, SearchModal, ShortcutsModal, Toasts } from "./Overlays";
import { Settings } from "./Settings";
import { Sidebar } from "./Sidebar";

/** Fallback poll: every 30 s while the realtime socket is down, every 5 min while it is up. */
const POLL_MS = 30_000;
const POLL_CONNECTED_MS = 5 * 60_000;

/** `ld.pollMs` (localStorage) overrides both intervals; E2E tests use it to rule the poll out. */
function pollOverride(): number | null {
  try {
    const v = Number(localStorage.getItem("ld.pollMs"));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Load the cached store, sync now, then: on `sync_needed` from the realtime socket, on `online`,
 * on hidden → visible (throttled, see `sync/triggers.ts`; never on window focus), and on a
 * fallback poll whose interval depends on whether the socket is connected.
 */
function useSyncLifecycle() {
  const engine = useEngine();
  const client = useApp((s) => s.client);
  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive)
        void engine
          .sync()
          .then(() => throttle.markSuccess())
          .catch(() => undefined);
    };
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const schedulePoll = () => {
      if (pollTimer) clearTimeout(pollTimer);
      const ms = pollOverride() ?? (realtime.connected ? POLL_CONNECTED_MS : POLL_MS);
      pollTimer = setTimeout(() => {
        if (document.visibilityState === "visible") run();
        schedulePoll();
      }, ms);
    };
    const realtime = new RealtimeClient({
      getUrl: async (fresh) => {
        const cached = engine.state.user?.websocket_url;
        if (cached && !fresh) return cached;
        return (await client.get<User>("/user")).websocket_url || null;
      },
      onSyncNeeded: run,
      onConnectedChange: schedulePoll,
    });
    const throttle = new VisibilitySyncThrottle({ isConnected: () => realtime.connected });
    void engine.init().then(() => {
      run();
      realtime.start(navigator.onLine !== false);
    });
    const onOnline = () => {
      realtime.setOnline(true);
      run();
    };
    const onOffline = () => realtime.setOnline(false);
    const detachVisible = onBecameVisible(document, () => {
      if (throttle.shouldSync()) run();
    });
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    schedulePoll();
    return () => {
      alive = false;
      realtime.stop();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      detachVisible();
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [engine, client]);
}

function InboxRoute() {
  const state = useSyncState();
  const id = state.user?.inbox_project_id ?? Object.values(state.projects).find((p) => p.inbox_project)?.id;
  if (!id) return <Loading />;
  return <ProjectView projectId={id} />;
}

function ProjectRoute() {
  const { id = "" } = useParams();
  const engine = useEngine();
  const state = useSyncState();
  const navigate = useNavigate();
  const real = engine.resolveId(id);
  useEffect(() => {
    if (real !== id) navigate(`/app/project/${real}`, { replace: true });
  }, [real, id, navigate]);
  if (!state.projects[real] && state.syncToken === "*") return <Loading />;
  if (state.projects[real]?.inbox_project) return <Navigate to="/app/inbox" replace />;
  return <ProjectView key={real} projectId={real} />;
}

function LabelRoute() {
  const { name = "" } = useParams();
  return <LabelView name={decodeURIComponent(name)} />;
}

function FilterRoute() {
  const { id = "" } = useParams();
  const engine = useEngine();
  const navigate = useNavigate();
  useSyncState(); // re-render when a temp id gets mapped
  const real = engine.resolveId(id);
  useEffect(() => {
    if (real !== id) navigate(`/app/filter/${real}`, { replace: true });
  }, [real, id, navigate]);
  return <FilterView key={real} filterId={real} />;
}

function TaskLinkRoute() {
  const { id = "" } = useParams();
  return <Navigate to={`/app/inbox?task=${id}`} replace />;
}

function Loading() {
  return <div className="empty-state">…</div>;
}

function StatusLine() {
  const status = useApp((s) => s.status);
  const t = useT();
  if (status === "offline") return <span className="sync-dot">{t("offline")}</span>;
  return null;
}

export function AppShell() {
  useSyncLifecycle();
  useKeyboardShortcuts();
  const sidebarOpen = useApp((s) => s.sidebarOpen);
  const set = useApp((s) => s.set);
  const [params] = useSearchParams();
  const location = useLocation();
  const t = useT();
  const taskId = params.get("task");
  const boardMode = location.pathname.startsWith("/app/project/") || location.pathname === "/app/inbox";
  return (
    <div className="shell">
      <Sidebar />
      <main className="main">
        <div className="topbar">
          <div className="left">
            {(!sidebarOpen || window.innerWidth < 750) && (
              <button
                type="button"
                className="icon-btn"
                onClick={() => set({ sidebarOpen: true })}
                aria-label={t("openSidebar")}
                data-testid="sidebar-open"
              >
                <IconSidebar size={22} />
              </button>
            )}
          </div>
          <div className="right">
            <StatusLine />
          </div>
        </div>
        <div className={`content${boardMode ? " board-mode" : ""}`} id="content">
          <Routes>
            <Route path="inbox" element={<InboxRoute />} />
            <Route path="today" element={<TodayView />} />
            <Route path="upcoming" element={<UpcomingView />} />
            <Route path="project/:id" element={<ProjectRoute />} />
            <Route path="label/:name" element={<LabelRoute />} />
            <Route path="filter/:id" element={<FilterRoute />} />
            <Route path="filters-labels" element={<FiltersLabelsView />} />
            <Route path="completed" element={<CompletedView />} />
            <Route path="task/:id" element={<TaskLinkRoute />} />
            <Route path="*" element={<Navigate to="/app/today" replace />} />
          </Routes>
        </div>
      </main>
      {taskId && <TaskDetail key={taskId} taskId={taskId} />}
      <QuickAddModal />
      <SearchModal />
      <Settings />
      <ShortcutsModal />
      <Toasts />
    </div>
  );
}
